/**
 * AccountButton — small component in Transport bar.
 * Shows "Sign In" when not logged in, or token balance pill with dropdown when logged in.
 */

import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccountStore } from '@/store/accountStore.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { signOut } from '@/api/accountApi.ts';
import { capabilities } from '@/capabilities.ts';
import { enqueueSendToNative } from '@/bridge/embeddedBridge.ts';
import { LOW_BALANCE_THRESHOLD } from '@/ai/tokenCosts.ts';
import styles from './AccountButton.module.css';

export function AccountButton({ dropdownDirection = 'above' }: { dropdownDirection?: 'above' | 'below' }) {
  const { t } = useTranslation('account');
  const accountUser = useAccountStore(s => s.accountUser);
  const accountBalance = useAccountStore(s => s.accountBalance);
  const setAccountUser = useAccountStore(s => s.setAccountUser);
  const setAccountBalance = useAccountStore(s => s.setAccountBalance);
  const setAuthDialogOpen = useAccountStore(s => s.setAuthDialogOpen);
  const setTokenPurchaseDialogOpen = useAccountStore(s => s.setTokenPurchaseDialogOpen);
  const setEarnCreditsDialogOpen = useAccountStore(s => s.setEarnCreditsDialogOpen);
  const standaloneAuthMode = useAccountStore(s => s.standaloneAuthMode);
  const toggleHistoryPanel = useUiStore(s => s.toggleHistoryPanel);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [flash, setFlash] = useState<'credit' | 'debit' | null>(null);
  const prevBalanceRef = useRef<number | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownShift, setDropdownShift] = useState(0);

  // Keep the panel fully on-screen. It is right-anchored to the pill (right: 0), and on
  // phone-portrait the pill sits at the LEFT edge of the transport's wrapped row, so the
  // 200px+ panel would hang off the left viewport edge (QA 2026-08-13 issue 8). Measure and
  // translate — anchor-position-agnostic, so the FileLoader (right-aligned) usage is unaffected.
  useLayoutEffect(() => {
    if (!dropdownOpen) { setDropdownShift(0); return; }
    const clamp = () => {
      const panel = dropdownRef.current;
      const anchor = wrapperRef.current;
      if (!panel || !anchor) return;
      const margin = 8;
      const a = anchor.getBoundingClientRect();      // transform-free: transform lives on the panel
      const w = panel.offsetWidth;                   // offsetWidth ignores our own transform
      // Un-shifted geometry: right edge = a.right, left edge = a.right - w
      let shift = 0;
      if (a.right - w < margin) shift = margin - (a.right - w);              // push rightward
      shift = Math.min(shift, window.innerWidth - margin - a.right);         // never past right edge
      setDropdownShift(shift);
    };
    clamp();
    window.addEventListener('resize', clamp);
    return () => window.removeEventListener('resize', clamp);
  }, [dropdownOpen]);

  // Detect balance changes and trigger flash animation
  useEffect(() => {
    const current = accountBalance?.available ?? null;
    const prev = prevBalanceRef.current;
    prevBalanceRef.current = current;
    if (prev !== null && current !== null && current !== prev) {
      setFlash(current > prev ? 'credit' : 'debit');
      const timer = setTimeout(() => setFlash(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [accountBalance?.available]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  if (!accountUser) {
    // Embedded: no web sign-in affordance — the launch gate + setAuth re-push make this
    // state transient; the forced sign-in surface is native-hosted (owner rule 2026-07-06).
    // Editor-standalone-auth carve-out: standaloneAuthMode means the web AuthDialog is the
    // only working sign-in (Unity can't do Apple auth in-Editor) — keep the button then.
    if (capabilities.bridge && !standaloneAuthMode) return null;
    return (
      <button
        className={styles.signInBtn}
        onClick={() => setAuthDialogOpen(true)}
      >
        {t('signInButton')}
      </button>
    );
  }

  const balance = accountBalance;
  const isLow = balance && !balance.isBeta && balance.available < LOW_BALANCE_THRESHOLD;

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        className={`${styles.balancePill} ${isLow ? styles.lowBalance : ''} ${flash === 'credit' ? styles.flashCredit : ''} ${flash === 'debit' ? styles.flashDebit : ''}`}
        onClick={() => setDropdownOpen(!dropdownOpen)}
      >
        {balance
          ? <><span className={styles.pillText}>{balance.available.toLocaleString()}</span>{' \u{1FA99}'}</>
          : '...'
        }
      </button>

      {dropdownOpen && (
        <div
          ref={dropdownRef}
          className={dropdownDirection === 'below' ? styles.dropdownBelow : styles.dropdownAbove}
          style={dropdownShift ? { transform: `translateX(${dropdownShift}px)` } : undefined}
        >
          <div className={styles.dropdownEmail}>{accountUser.email || accountUser.displayName || t('signedInFallback')}</div>
          {balance && (
            <div className={styles.dropdownBalance}>
              <span className={styles.dropdownBalanceNum}>{balance.available.toLocaleString()}</span> {t('creditsLabel')}
            </div>
          )}
          <button
            className={isLow ? styles.dropdownItemUrgent : styles.dropdownItemAccent}
            onClick={() => {
              setTokenPurchaseDialogOpen(true);
              setDropdownOpen(false);
            }}
          >
            {isLow ? t('addCreditsLow') : t('addCredits')}
          </button>
          <button
            className={styles.dropdownItem}
            onClick={() => { setEarnCreditsDialogOpen(true); setDropdownOpen(false); }}
          >
            {t('earnCreditsReferral')}
          </button>
          <button
            className={styles.dropdownItem}
            onClick={() => { toggleHistoryPanel(); setDropdownOpen(false); }}
          >
            {t('aiGenHistory')}
          </button>
          <button
            className={styles.dropdownItemDanger}
            onClick={async () => {
              const standaloneAuth = useAccountStore.getState().standaloneAuthMode;
              if (capabilities.bridge && !standaloneAuth) {
                // Normal embedded — sign out via Unity bridge
                setAccountUser(null);
                setAccountBalance(null);
                useAccountStore.getState().setNativeAccessToken(null);
                enqueueSendToNative('signOut', {});
              } else {
                // Standalone or editor standalone auth
                await signOut();
                setAccountUser(null);
                setAccountBalance(null);
              }
              setDropdownOpen(false);
            }}
          >
            {t('signOutButton')}
          </button>
        </div>
      )}
    </div>
  );
}
